/**
 * Blob Sink Action API Service - Enhanced with cache invalidation
 *
 * This service wraps the generated blob sink action API with proper cache
 * invalidation for approve/reject mutations.
 */

import { baseApiClient } from './baseService';
import { enhancedApi as generatedApi } from './generated/blobSinkActionService';

// ------------------------------------------------------------------
// Dropbox import mutation (manually defined until codegen catches up)
// ------------------------------------------------------------------

export interface DropboxImportResultItem {
  dropbox_document_id: string;
  blob_id: string | null;
  status: 'created' | 'existing' | 'failed';
  error: string | null;
}

export interface DropboxImportResponse {
  results: DropboxImportResultItem[];
}

export interface DropboxImportApiArg {
  matterId: string;
  dropboxDocumentIds: string[];
}

export interface DropboxRefreshTreeApiArg {
  matterId: string;
}

export interface ClioRefreshTreeApiArg {
  matterId: string;
}

export interface ClioImportApiArg {
  matterId: string;
  clioDocumentIds: string[];
}

export interface GoogleDriveImportApiArg {
  matterId: string;
  googleDriveDocumentIds: string[];
}

export interface GoogleDriveRefreshTreeApiArg {
  matterId: string;
}

const dropboxImportApi = baseApiClient.injectEndpoints({
  endpoints: (build) => ({
    dropboxImportDocuments: build.mutation<
      DropboxImportResponse,
      DropboxImportApiArg
    >({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/dropbox/import/`,
        method: 'POST',
        body: { dropbox_document_ids: queryArg.dropboxDocumentIds },
      }),
    }),
    clioImportDocuments: build.mutation<
      DropboxImportResponse,
      ClioImportApiArg
    >({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/clio/import/`,
        method: 'POST',
        body: { clio_document_ids: queryArg.clioDocumentIds },
      }),
    }),
    dropboxRefreshTree: build.mutation<unknown, DropboxRefreshTreeApiArg>({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/dropbox/refresh/`,
        method: 'POST',
      }),
    }),
    clioRefreshTree: build.mutation<unknown, ClioRefreshTreeApiArg>({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/clio/refresh/`,
        method: 'POST',
      }),
    }),
    googleDriveImportDocuments: build.mutation<
      DropboxImportResponse,
      GoogleDriveImportApiArg
    >({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/google-drive/import/`,
        method: 'POST',
        body: { google_drive_document_ids: queryArg.googleDriveDocumentIds },
      }),
    }),
    googleDriveRefreshTree: build.mutation<unknown, GoogleDriveRefreshTreeApiArg>({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/google-drive/refresh/`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useDropboxImportDocumentsMutation,
  useClioImportDocumentsMutation,
  useDropboxRefreshTreeMutation,
  useClioRefreshTreeMutation,
  useGoogleDriveImportDocumentsMutation,
  useGoogleDriveRefreshTreeMutation,
} = dropboxImportApi;

// ------------------------------------------------------------------
// Enhanced generated API with cache invalidation
// ------------------------------------------------------------------

/**
 * Enhanced version of the generated API with proper cache invalidation.
 *
 * This ensures that when sink action mutations are performed (approve/reject),
 * the cache is properly invalidated to refetch updated data.
 */
export const blobSinkActionService = generatedApi.enhanceEndpoints({
  addTagTypes: ['blob-sink-actions', 'Blobs'],
  endpoints: {
    blobSinkActionsList: {
      providesTags: (result, error, { blobId }) => [
        { type: 'blob-sink-actions', id: blobId },
        'blob-sink-actions',
      ],
    },
    blobSinkActionsRetrieve: {
      providesTags: (result, error, { blobId, actionId }) => [
        { type: 'blob-sink-actions', id: `${blobId}-${actionId}` },
        { type: 'blob-sink-actions', id: blobId },
        'blob-sink-actions',
      ],
    },
    blobSinkActionsApprove: {
      invalidatesTags: (result, error, { blobId, matterId }) => [
        { type: 'blob-sink-actions', id: blobId },
        'blob-sink-actions',
        { type: 'Blobs', id: matterId },
        'Blobs',
      ],
    },
    blobSinkActionsReject: {
      invalidatesTags: (result, error, { blobId, matterId }) => [
        { type: 'blob-sink-actions', id: blobId },
        'blob-sink-actions',
        { type: 'Blobs', id: matterId },
        'Blobs',
      ],
    },
    blobSinkActionsRemove: {
      invalidatesTags: (result, error, { blobId, matterId }) => [
        { type: 'blob-sink-actions', id: blobId },
        'blob-sink-actions',
        { type: 'Blobs', id: matterId },
        'Blobs',
      ],
    },
    blobSinkActionsRepush: {
      invalidatesTags: (result, error, { blobId, matterId }) => [
        { type: 'blob-sink-actions', id: blobId },
        'blob-sink-actions',
        { type: 'Blobs', id: matterId },
        'Blobs',
      ],
    },
  },
});

// Re-export types from generated service
export * from './generated/blobSinkActionService';
